import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../components/Icon';
import { useChat } from '../ChatContext';
import { formatDuration, parseWaveform } from '../chatUtils';

// Inline-проигрывание голосового: волна из waveform, таймер, клик по волне — перемотка.
const VoiceMessage = ({ message }) => {
  const { refetchMessage } = useChat();
  const audioRef = useRef(null);
  const retriedRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [positionMs, setPositionMs] = useState(0);

  const peaks = parseWaveform(message.waveform);
  const totalMs = message.durationMs || 0;
  const progress = totalMs > 0 ? Math.min(1, positionMs / totalMs) : 0;

  useEffect(() => {
    retriedRef.current = false;
  }, [message.attachment?.url]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      return;
    }
    try {
      await audio.play();
    } catch {
      await refreshSource(true);
    }
  };

  /**
   * Presigned-ссылка живёт 30 минут: если пользователь открыл чат и нажал play
   * позже, и play(), и событие error приведут сюда — тихо перезапрашиваем
   * сообщение через историю и подставляем свежий url.
   */
  const refreshSource = async (autoPlay) => {
    const audio = audioRef.current;
    if (!audio || retriedRef.current || message.id == null) return;
    retriedRef.current = true;
    const fresh = await refetchMessage(message.chatId, message.id);
    if (!fresh?.attachment?.url) return;
    audio.src = fresh.attachment.url;
    audio.load();
    if (autoPlay) audio.play().catch(() => {});
  };

  const seek = (event) => {
    const audio = audioRef.current;
    if (!audio || !totalMs) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    audio.currentTime = (totalMs / 1000) * ratio;
    setPositionMs(totalMs * ratio);
  };

  return (
    <div className="chat-voice">
      <button type="button" className="chat-voice-play" onClick={togglePlay}>
        <Icon name={playing ? 'pause' : 'play'} size={16} />
      </button>

      <div className="chat-voice-wave" onClick={seek} role="presentation">
        {(peaks.length ? peaks : new Array(32).fill(20)).map((peak, index) => {
          const played = peaks.length ? index / peaks.length <= progress : false;
          return (
            <span
              key={index}
              className={`chat-voice-bar${played ? ' played' : ''}`}
              style={{ height: `${Math.max(12, peak)}%` }}
            />
          );
        })}
      </div>

      <span className="chat-voice-time">
        {formatDuration(playing || positionMs ? totalMs - positionMs : totalMs)}
      </span>

      <audio
        ref={audioRef}
        src={message.attachment?.url || ''}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setPositionMs(0);
        }}
        onTimeUpdate={(e) => setPositionMs(e.target.currentTime * 1000)}
        onError={() => refreshSource(false)}
      />
    </div>
  );
};

export default VoiceMessage;
